import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, LogOut, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { LOGO_URL } from "../lib/constants";

const STATUS_STYLE = {
  novo: "bg-[#E60000] text-white",
  lido: "bg-white/10 text-white/70",
  atendido: "bg-white text-[#0A0A0A]",
};

export default function AdminDashboard() {
  const [quotes, setQuotes] = useState(null);
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const [q, s] = await Promise.all([api.get("/admin/quotes"), api.get("/admin/stats")]);
      setQuotes(q.data);
      setStats(s.data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/admin");
      else toast.error("Erro ao carregar dados");
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/quotes/${id}`, { status });
      setQuotes((q) => q.map((item) => (item.id === id ? { ...item, status } : item)));
    } catch {
      toast.error("Não foi possível atualizar");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/quotes/${id}`);
      setQuotes((q) => q.filter((item) => item.id !== id));
      toast.success("Orçamento removido");
    } catch {
      toast.error("Não foi possível remover");
    }
  };

  const logout = async () => {
    await api.post("/auth/logout");
    navigate("/admin");
  };

  const novos = quotes?.filter((q) => q.status === "novo").length ?? 0;

  return (
    <div className="min-h-screen bg-[#171717] text-white" data-testid="admin-dashboard">
      <header className="bg-[#0A0A0A] border-b border-white/10 px-6 py-4 flex items-center justify-between" data-testid="admin-header">
        <div className="flex items-center gap-4">
          <img src={LOGO_URL} alt="Diggy Shop" className="h-9 w-auto" />
          <span className="text-xs tracking-[0.3em] font-bold text-white/50 hidden sm:block">PAINEL ADMIN</span>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/" data-testid="admin-view-site-link" className="text-xs tracking-[0.15em] text-white/50 hover:text-[#E60000] transition-colors duration-300">VER SITE</Link>
          <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-2 text-xs tracking-[0.15em] font-bold text-white/70 hover:text-[#E60000] transition-colors duration-300">
            <LogOut size={14} /> SAIR
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <p className="text-xs tracking-[0.3em] font-bold text-[#E60000] mb-2">ORÇAMENTOS</p>
            <h1 className="font-display font-bold text-4xl leading-none">Pedidos recebidos</h1>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-[#0A0A0A] border border-white/10 px-5 py-3 text-center" data-testid="stats-visits">
              <p className="font-display font-black text-2xl">{stats ? stats.visits : "—"}</p>
              <p className="text-[10px] tracking-[0.2em] text-white/50">VISITAS</p>
              {stats ? <p className="text-[10px] text-[#E60000] mt-1">+{stats.visits_today} hoje</p> : null}
            </div>
            <div className="bg-[#0A0A0A] border border-white/10 px-5 py-3 text-center" data-testid="stats-clicks">
              <p className="font-display font-black text-2xl">{stats ? stats.whatsapp_clicks : "—"}</p>
              <p className="text-[10px] tracking-[0.2em] text-white/50">CLIQUES WHATSAPP</p>
              {stats ? <p className="text-[10px] text-[#E60000] mt-1">+{stats.whatsapp_clicks_today} hoje</p> : null}
            </div>
            <div className="bg-[#0A0A0A] border border-white/10 px-5 py-3 text-center" data-testid="stats-total">
              <p className="font-display font-black text-2xl">{quotes?.length ?? "—"}</p>
              <p className="text-[10px] tracking-[0.2em] text-white/50">ORÇAMENTOS</p>
            </div>
            <div className="bg-[#E60000] px-5 py-3 text-center" data-testid="stats-new">
              <p className="font-display font-black text-2xl">{quotes ? novos : "—"}</p>
              <p className="text-[10px] tracking-[0.2em]">NOVOS</p>
            </div>
          </div>
        </div>

        {stats && stats.sources && stats.sources.length > 0 && (
          <div className="bg-[#0A0A0A] border border-white/10 p-6 mb-10" data-testid="sources-panel">
            <p className="text-xs tracking-[0.3em] font-bold text-[#E60000] mb-5">ORIGEM DAS VISITAS</p>
            <div className="space-y-3">
              {stats.sources.map((s) => (
                <div key={s.source} className="flex items-center gap-4" data-testid={`source-row-${s.source.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
                  <span className="w-32 sm:w-40 text-sm text-white/70 truncate">{s.source}</span>
                  <div className="flex-1 h-2 bg-white/5">
                    <div className="h-2 bg-[#E60000]" style={{ width: `${Math.max(4, (s.count / stats.sources[0].count) * 100)}%` }} />
                  </div>
                  <span className="w-10 text-right text-sm font-bold">{s.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {quotes === null ? (
          <div className="flex justify-center py-24" data-testid="quotes-loading"><Loader2 className="animate-spin text-[#E60000]" size={32} /></div>
        ) : quotes.length === 0 ? (
          <div className="border border-white/10 bg-[#0A0A0A] py-24 text-center" data-testid="quotes-empty">
            <p className="font-display italic text-2xl text-white/60">Nenhum pedido por aqui ainda.</p>
            <p className="text-sm text-white/40 mt-2">Os orçamentos enviados pelo site aparecem nesta lista.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="quotes-list">
            {quotes.map((q) => (
              <article key={q.id} className="bg-[#0A0A0A] border border-white/10 p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`quote-card-${q.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-extrabold tracking-tight">{q.name}</h3>
                    <span className={`text-[10px] tracking-[0.2em] font-bold px-2 py-1 ${STATUS_STYLE[q.status] || STATUS_STYLE.novo}`} data-testid={`quote-status-${q.id}`}>
                      {q.status.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-sm text-white/50 mt-1">{q.phone} • {q.service}</p>
                  {q.message && <p className="text-sm text-white/70 mt-2 font-light">{q.message}</p>}
                  <p className="text-[10px] tracking-[0.2em] text-white/30 mt-3">
                    {new Date(q.created_at).toLocaleString("pt-BR")}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {q.status === "novo" && (
                    <button onClick={() => setStatus(q.id, "lido")} data-testid={`quote-mark-read-${q.id}`} className="text-[10px] tracking-[0.15em] font-bold border border-white/20 hover:border-[#E60000] hover:text-[#E60000] px-4 py-2 transition-colors duration-300">
                      MARCAR LIDO
                    </button>
                  )}
                  {q.status === "lido" && (
                    <button onClick={() => setStatus(q.id, "atendido")} data-testid={`quote-mark-done-${q.id}`} className="text-[10px] tracking-[0.15em] font-bold border border-white/20 hover:border-[#E60000] hover:text-[#E60000] px-4 py-2 transition-colors duration-300">
                      MARCAR ATENDIDO
                    </button>
                  )}
                  <button onClick={() => remove(q.id)} data-testid={`quote-delete-${q.id}`} className="p-2 text-white/40 hover:text-[#E60000] transition-colors duration-300" aria-label="Remover">
                    <Trash2 size={16} />
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
